import React, {
  forwardRef, ForwardedRef,
} from 'react';
import styled from 'styled-components';
import isEqual from 'react-fast-compare';

import { sortRows } from '../../utils/tableUtils';
import { TableHead, TableBody } from './components';
import Memo from '../Memo';
import Box from '../Box';
import LoadingSpinner from '../LoadingSpinner';
import {
  Column,
  Row,
  SortState,
} from '../../types';

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
`;

export type TableProps = {
  columns: Column[],
  rows: Row[],
  ariaLabel: string,
  loading?: boolean,
  height?: string,
  minRowHeight?: string,
  onClickRow?: (rowId: string) => void;
  emptyStateBody?: React.ReactNode;
  sortState: SortState;
  onSortByColumn: (columnId: string) => void;
  skipRowSort?: boolean;
  onTableUpdate?: () => void;
} & React.RefAttributes<HTMLDivElement>;

const Table: React.FC<TableProps> = forwardRef((props: TableProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    columns,
    rows,
    loading,
    ariaLabel,
    height,
    minRowHeight,
    emptyStateBody,
    onClickRow,
    sortState,
    onSortByColumn,
    skipRowSort,
    onTableUpdate,
  } = props;

  const sortedRows = skipRowSort
    ? rows
    : sortRows(rows, columns.find(col => col.id === sortState.columnId), sortState.direction);

  const handleClickSortIndicator = (columnId: string) => {
    if (onTableUpdate) {
      onTableUpdate();
    }
    onSortByColumn(columnId);
  };

  return (
    <Box ref={ref} height={height ?? '100%'} width="100%" overflow="auto" borderBottom="1px solid #F0F0F0">
      <StyledTable aria-label={ariaLabel}>
        <TableHead
          columns={columns}
          sortOrder={sortState}
          onClickSortIndicator={handleClickSortIndicator}
        />
        <TableBody
          rows={sortedRows}
          columns={columns}
          onClickRow={onClickRow}
          minRowHeight={minRowHeight}
        />
      </StyledTable>
      {loading && (
        <Box height="100%" width="100%" display="grid" alignContent="space-around" justifyContent="space-around">
          <LoadingSpinner size="150px" />
        </Box>
      )}
      {(rows.length === 0 && emptyStateBody) && (
        <Box width="100%" alignContent="center">
          {' '}
          {emptyStateBody}
          {' '}
        </Box>
      )}
    </Box>
  );
});

export default Memo(Table, isEqual);
